import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { PhotoIcon, MicrophoneIcon, StopIcon, PaperAirplaneIcon, ArrowUpTrayIcon, DocumentTextIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { uploadAudio, uploadImage, uploadedImageUrl, uploadUnterlage, type ImageUpload, type Unterlage } from '../../api/unimedica';

// Port aus chat/Composer.svelte — Textarea + Bild-Upload (Datei, Drag&Drop,
// Einfuegen) + Diktat (Whisper) + Senden. Haelt seinen eigenen Multimodal-
// Zustand; der Parent bekommt beim Senden nur (text, imageIds, imageSources).

export type ComposerHandle = { focus: () => void };

const Punkte = ({ hell }: { hell?: boolean }) => (
  <span className="flex gap-1">{[0, 0.2, 0.4].map((d) => <span key={d} className={`pulse-dot h-1.5 w-1.5 rounded-full ${hell ? 'bg-white' : 'bg-primary-500'}`} style={{ animationDelay: `${d}s` }} />)}</span>
);

const Composer = forwardRef<ComposerHandle, {
  streaming: boolean;
  placeholder: string;
  onSubmit: (text: string, imageIds: string[], imageSources: { image_id: string }[], unterlageIds?: string[]) => Promise<void> | void;
}>(({ streaming, placeholder, onSubmit }, ref) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }));

  const [input, setInput] = useState('');
  const [attachedImages, setAttachedImages] = useState<ImageUpload[]>([]);
  const [unterlagen, setUnterlagen] = useState<Unterlage[]>([]);
  const [uploadingUnterlage, setUploadingUnterlage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const send = async () => {
    if (streaming || recording || transcribing) return;
    if (!input.trim() && attachedImages.length === 0 && unterlagen.length === 0) return;
    const text = input, ids = attachedImages.map((i) => i.file_id), srcs = attachedImages.map((i) => ({ image_id: i.file_id }));
    const uids = unterlagen.filter((u) => u.gelesen).map((u) => u.file_id);
    setInput(''); setAttachedImages([]); setUnterlagen([]);
    await onSubmit(text, ids, srcs, uids);
  };

  const handleImageFiles = async (files: File[] | FileList | null) => {
    if (!files) return;
    setImageError(null); setUploadingImage(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) { setImageError(`Datei "${file.name}" ist kein Bild`); continue; }
        const r = await uploadImage(file);
        setAttachedImages((a) => [...a, r]);
      }
    } catch (e: any) { setImageError(e.message || 'Bild-Upload fehlgeschlagen'); }
    finally { setUploadingImage(false); }
  };

  /* Hineinziehen (#1, Mario 08.10.: Visitenblatt als PDF ging nicht — „ist kein Bild“): nach Dateityp verteilen.
     Bilder (jpg/png/webp/gif) ans Bildmodell, alles Lesbare (PDF, Word, TIFF/HEIC-Scans, Text) als Unterlage. */
  const UNTERLAGE_ENDUNGEN = ['.pdf', '.docx', '.odt', '.tiff', '.tif', '.heic', '.csv', '.md', '.txt'];
  const verteileDateien = async (files: FileList) => {
    const bilder: File[] = [], unterlagen: File[] = [], fremd: string[] = [];
    for (const f of Array.from(files)) {
      const name = f.name.toLowerCase();
      if (UNTERLAGE_ENDUNGEN.some((x) => name.endsWith(x))) unterlagen.push(f);
      else if (f.type.startsWith('image/')) bilder.push(f);
      else fremd.push(f.name);
    }
    if (bilder.length) await handleImageFiles(bilder);
    if (unterlagen.length) await handleUnterlagen(unterlagen);
    if (fremd.length) setImageError(`Nicht unterstützt: ${fremd.join(', ')} — möglich sind Bilder, PDF, Word und Textdateien.`);
  };

  /* Unterlagen sind alles, was gelesen statt angeschaut werden soll: PDF, Scan, Formular.
     Sie gehen NICHT ans Bildmodell, sondern durch Docling bzw. Apple Vision OCR — an 20 echten
     Praxisdokumenten gemessen deutlich zuverlaessiger (Entscheid 12.09.). */
  const handleUnterlagen = async (files: File[] | FileList | null) => {
    if (!files) return;
    setImageError(null); setUploadingUnterlage(true);
    try {
      for (const file of Array.from(files)) {
        const r = await uploadUnterlage(file);
        setUnterlagen((u) => [...u, r]);
      }
    } catch (e: any) { setImageError(e.message || 'Die Unterlage konnte nicht gelesen werden'); }
    finally { setUploadingUnterlage(false); }
  };

  const onPaste = async (e: React.ClipboardEvent) => {
    const files: File[] = [];
    for (const item of Array.from(e.clipboardData?.items ?? [])) if (item.type.startsWith('image/')) { const f = item.getAsFile(); if (f) files.push(f); }
    if (files.length) { e.preventDefault(); await handleImageFiles(files); }
  };

  const startRecording = async () => {
    setRecordingError(null);
    if (!navigator.mediaDevices?.getUserMedia) { setRecordingError('Diktat ist auf diesem Gerät gerade nicht möglich. Bitte die Praxis-IT kontaktieren.'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setTranscribing(true);
        try {
          const r = await uploadAudio(new Blob(chunksRef.current, { type: 'audio/webm' }), 'de');
          setInput((cur) => (cur.trim() ? cur.trimEnd() + ' ' : '') + r.text);
          setTimeout(() => { const el = inputRef.current; if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); } }, 0);
        } catch (e: any) { setRecordingError(e.message || 'Transkription fehlgeschlagen'); }
        finally { setTranscribing(false); }
      };
      rec.start(); recorderRef.current = rec; setRecording(true);
    } catch (e: any) { setRecordingError(e.message || 'Mikrofon konnte nicht aktiviert werden'); setRecording(false); }
  };
  const stopRecording = () => { const r = recorderRef.current; if (r && r.state !== 'inactive') r.stop(); setRecording(false); };

  const disabled = streaming || recording || transcribing;
  const Fehler = ({ text, onOk }: { text: string; onOk: () => void }) => (
    <div className="flex items-center gap-2 text-[12px] text-rose-700"><span>⚠</span><span>{text}</span><button className="ml-auto text-[11px] underline" onClick={onOk}>OK</button></div>
  );

  return (
    <footer className="relative border-t border-gray-200 bg-white"
      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }} onDragLeave={() => setDragActive(false)}
      onDrop={async (e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer?.files) await verteileDateien(e.dataTransfer.files); }}>
      {dragActive && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center border-2 border-dashed border-primary-500 bg-primary-50/95">
          <div className="text-center"><ArrowUpTrayIcon className="mx-auto mb-2 h-9 w-9 text-primary-500" /><div className="text-[14px] font-medium text-primary-500">Bild oder Unterlage hier loslassen</div><div className="mt-0.5 text-[12px] text-primary-500/80">PDF, Word, Scan, Foto</div></div>
        </div>
      )}
      <div className="w-full space-y-3 px-4 py-4 sm:px-7">
        {(attachedImages.length > 0 || uploadingImage) && (
          <div className="flex flex-wrap gap-2">
            {attachedImages.map((img) => (
              <div key={img.file_id} className="group relative">
                <img src={uploadedImageUrl(img.file_id)} alt={img.filename} className="h-20 w-20 rounded-md border border-gray-200 object-cover" />
                <button type="button" onClick={() => setAttachedImages((a) => a.filter((i) => i.file_id !== img.file_id))} aria-label="Bild entfernen"
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100">×</button>
                <div className="absolute inset-x-0 bottom-0 truncate rounded-b-md bg-black/60 px-1 py-0.5 text-center text-[9px] text-white">{img.filename}</div>
              </div>
            ))}
            {uploadingImage && <div className="flex h-20 w-20 items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50"><Punkte /></div>}
          </div>
        )}
        {(unterlagen.length > 0 || uploadingUnterlage) && (
          <div className="flex flex-col gap-1.5">
            {unterlagen.map((u) => (
              <div key={u.file_id} className={`flex items-start gap-2 rounded-md border px-2.5 py-2 text-[12px] ${
                u.gelesen ? 'border-gray-200 bg-gray-50' : 'border-rose-200 bg-rose-50'}`}>
                <DocumentTextIcon className={`mt-0.5 h-4 w-4 shrink-0 ${u.gelesen ? 'text-ps-dark-400' : 'text-rose-500'}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium text-ps-dark-800">{u.filename}</span>
                    {u.gelesen
                      ? <span className="text-[11px] text-ps-dark-400">{u.zeichen.toLocaleString('de-CH')} Zeichen gelesen</span>
                      : <span className="text-[11px] font-medium text-rose-700">nicht lesbar</span>}
                  </div>
                  {u.gelesen && u.vorschau && <div className="truncate text-[11px] text-ps-dark-500">{u.vorschau}</div>}
                  {u.hinweise.map((h, i) => (
                    <div key={i} className="mt-0.5 flex items-start gap-1 text-[11px] text-amber-800">
                      <ExclamationTriangleIcon className="mt-px h-3 w-3 shrink-0" /><span>{h}</span>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => setUnterlagen((a) => a.filter((x) => x.file_id !== u.file_id))}
                  aria-label="Unterlage entfernen" className="shrink-0 px-1 text-ps-dark-400 hover:text-rose-600">×</button>
              </div>
            ))}
            {uploadingUnterlage && (
              <div className="flex items-center gap-2 rounded-md border border-dashed border-gray-200 bg-gray-50 px-2.5 py-2 text-[12px] text-ps-dark-500">
                <Punkte /> <span>Unterlage wird gelesen …</span>
              </div>
            )}
          </div>
        )}
        {recordingError && <Fehler text={recordingError} onOk={() => setRecordingError(null)} />}
        {imageError && <Fehler text={imageError} onOk={() => setImageError(null)} />}

        <form onSubmit={(e) => { e.preventDefault(); void send(); }}>
          <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onPaste={onPaste} rows={3} disabled={disabled}
            placeholder={recording ? 'Aufnahme läuft … Mikrofon stoppen mit dem Knopf unten' : transcribing ? 'Transkribiere Sprache zu Text …' : placeholder}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
            className="input-field py-3 !text-[15px]" />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              <label className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-md border border-gray-200 px-3 text-[12.5px] font-medium text-ps-dark-700 transition-colors hover:border-primary-500 hover:bg-primary-50 hover:text-primary-500" title="Bild hochladen (oder ins Eingabefeld einfügen mit Cmd+V)">
                <PhotoIcon className="h-4 w-4" /><span className="hidden sm:inline">Bild</span>
                <input type="file" accept="image/*" multiple className="hidden" disabled={streaming || uploadingImage} onChange={(e) => void handleImageFiles(e.currentTarget.files)} />
              </label>
              <label className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-md border border-gray-200 px-3 text-[12.5px] font-medium text-ps-dark-700 transition-colors hover:border-primary-500 hover:bg-primary-50 hover:text-primary-500" title="PDF, Scan oder Formular — wird gelesen und der Frage vorangestellt">
                <DocumentTextIcon className="h-4 w-4" /><span className="hidden sm:inline">Unterlage</span>
                <input type="file" accept=".pdf,.docx,.odt,.png,.jpg,.jpeg,.webp,.tiff,.tif,.heic,.csv,.md,.txt" multiple className="hidden"
                  disabled={streaming || uploadingUnterlage} onChange={(e) => { void handleUnterlagen(e.currentTarget.files); e.currentTarget.value = ''; }} />
              </label>
              <button type="button" onClick={recording ? stopRecording : startRecording} disabled={streaming || transcribing}
                title={recording ? 'Aufnahme stoppen' : transcribing ? 'Transkribiere …' : 'Diktieren'} aria-label={recording ? 'Aufnahme stoppen' : 'Diktieren starten'}
                className={`inline-flex h-10 items-center gap-1.5 rounded-md border px-3 text-[12.5px] font-medium transition-colors ${recording
                  ? 'animate-pulse border-rose-500 bg-rose-500 text-white'
                  : transcribing ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-gray-200 text-ps-dark-700 hover:border-primary-500 hover:bg-primary-50 hover:text-primary-500'}`}>
                {transcribing ? <><span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-amber-300 border-t-amber-700" /><span className="hidden sm:inline">Transkribiere</span></>
                  : recording ? <><StopIcon className="h-3.5 w-3.5" /><span>Aufnahme stoppen</span></>
                  : <><MicrophoneIcon className="h-4 w-4" /><span className="hidden sm:inline">Diktieren</span></>}
              </button>
              <span className="ml-2 hidden items-center gap-1.5 text-[11.5px] text-ps-dark-400 lg:inline-flex">
                <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10.5px]">Enter</kbd><span>senden</span><span className="mx-1">·</span>
                <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10.5px]">Shift+Enter</kbd><span>neue Zeile</span>
              </span>
            </div>
            <button type="submit" disabled={disabled || (!input.trim() && attachedImages.length === 0 && unterlagen.length === 0)} className="btn-primary flex h-10 items-center gap-2 px-5 text-[13.5px] font-semibold">
              {streaming ? <Punkte hell /> : <><span>Senden</span><PaperAirplaneIcon className="h-3.5 w-3.5" /></>}
            </button>
          </div>
        </form>
      </div>
    </footer>
  );
});
Composer.displayName = 'Composer';
export default Composer;
